import { useEffect, useRef, useState, type RefObject } from 'react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

// How far past the viewport edge the followed row has to sit before the button appears
const OFF_SCREEN_MARGIN_PX = 240;

interface ScrollToCurrentTimeButtonProps {
  scrollerRef: RefObject<HTMLDivElement | null>;
  rowIndex: number;
  getRowBounds: (index: number) => { start: number; end: number } | undefined;
  onActivate: () => void;
  resizeKey: unknown;
}

/**
 * Floating button that takes the reader back to the row currently being spoken, shown only once
 * that row has scrolled well clear of the viewport.
 *
 * The check reads cached measurements plus `scrollTop`, so a scroll costs one rAF-throttled
 * comparison and re-renders only when the answer changes. `rowIndex` moves at block boundaries
 * rather than on every playback tick, so re-subscribing is rare.
 */
export function ScrollToCurrentTimeButton({
  scrollerRef,
  rowIndex,
  getRowBounds,
  onActivate,
  resizeKey,
}: ScrollToCurrentTimeButtonProps) {
  const [direction, setDirection] = useState<'above' | 'below' | null>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || rowIndex < 0) {
      setDirection(null);
      return;
    }

    let frame = 0;

    const update = () => {
      frame = 0;

      const bounds = getRowBounds(rowIndex);
      if (!bounds) {
        setDirection(null);
        return;
      }

      const viewTop = scroller.scrollTop;
      const viewBottom = viewTop + scroller.clientHeight;

      setDirection(
        bounds.end < viewTop - OFF_SCREEN_MARGIN_PX
          ? 'above'
          : bounds.start > viewBottom + OFF_SCREEN_MARGIN_PX
            ? 'below'
            : null,
      );
    };

    update();

    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollerRef, rowIndex, getRowBounds, resizeKey]);

  // Held over while the button fades out, so it doesn't swap sides and arrows on the way.
  const lastDirectionRef = useRef<'above' | 'below'>('below');
  if (direction) lastDirectionRef.current = direction;
  const shownDirection = direction ?? lastDirectionRef.current;

  return (
    // Stays mounted rather than rendering conditionally, so it has something to fade out from.
    <Button
      variant="white"
      size="sm"
      onClick={onActivate}
      aria-hidden={!direction}
      tabIndex={direction ? undefined : -1}
      className={cn(
        'absolute left-1/2 z-10 -translate-x-1/2 rounded-full px-3 py-1.5 shadow-lg/4 ring-offset-transparent transition-[opacity,color,background-color,border-color] duration-150 focus-visible:ring-offset-white',
        direction ? 'opacity-100' : 'pointer-events-none opacity-0',
        shownDirection === 'above' ? 'top-6' : 'bottom-6',
      )}
    >
      <Icon className={shownDirection === 'above' ? 'lucide--arrow-up' : 'lucide--arrow-down'} />
      Go to current time
    </Button>
  );
}
